import {
  LOCAL_SYNC_DB_NAME,
  LOCAL_SYNC_SCOPED_PREFIX,
  dropLocalSyncDatabase,
  dropScopedLocalSyncDatabases,
} from '@/local-sync/browserStorageDriver';
import { OWNER_RULE_STORES } from '@/generated/owner-rule-stores';
import {
  RUN_FILES_DB_NAME,
  dropRunFilesDatabase,
} from '@/features/dashboard/clarityRunFilesDatabase';
// Not the register itself (`storeRegister.ts`): its files and notes would be in the launch entry (T-0688).
import { entryCovers } from './storeEntryCovers';

/** Never dropped: it names whose stores these are, and is rewritten for the person signing in. */
const OWNER_KEY = 'featit:placeOwner';

/**
 * F-0534 PPD-2: drops, from each storage given, every key a person entry under the owner rule covers
 * (`STORE_REGISTER`). Called by `ProjectStore.adoptPerson` when a different person signs in. Keys are
 * read first and removed after, so a prefix entry never skips one.
 */
export function dropOwnerRuleStores(storages: readonly Storage[]): void {
  const entries = OWNER_RULE_STORES.filter((entry) => entry.key !== OWNER_KEY);
  for (const storage of storages) {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null) keys.push(key);
    }
    for (const key of keys) {
      if (entries.some((entry) => entryCovers(entry, key))) storage.removeItem(key);
    }
  }
}

/**
 * How each IndexedDB database under the owner rule is deleted (I-1244). A database entry with no
 * dropper here fails `ownerRule.test.ts`.
 */
export const OWNER_RULE_DATABASE_DROPPERS: Readonly<Record<string, () => Promise<void>>> = {
  [LOCAL_SYNC_DB_NAME]: dropLocalSyncDatabase,
  [`${LOCAL_SYNC_SCOPED_PREFIX}*`]: dropScopedLocalSyncDatabases,
  [RUN_FILES_DB_NAME]: dropRunFilesDatabase,
};

/** F-0534 PPD-2: deletes every IndexedDB database the register puts under the owner rule. */
export function dropOwnerRuleDatabases(): void {
  for (const entry of OWNER_RULE_STORES) {
    if (!entry.areas.includes('indexeddb')) continue;
    void OWNER_RULE_DATABASE_DROPPERS[entry.key]?.();
  }
}

/**
 * An account that no longer exists leaves nothing of itself on this device: every person store under
 * the owner rule and every local-sync database, as when another person signs in. Called when the
 * person's own account is erased (`AccountDeletionSettings`), before they are signed out.
 */
export function dropEverythingOfThisPerson(): void {
  dropOwnerRuleStores([localStorage, sessionStorage]);
  dropOwnerRuleDatabases();
}

/**
 * The owner rule on the device's own stores, as `adoptPerson` runs it when another person signs in. It
 * reaches the browser's real `localStorage` and `sessionStorage` even while an as-if view is held:
 * the rule is about what is on the device, not about what the view overlays.
 */
export function dropOwnerRuleStoresOfThisDevice(): void {
  dropOwnerRuleStores([localStorage, sessionStorage]);
}
